import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { ReactNode } from 'react';

export type FieldEditModalHeaderAction = {
  ariaLabel: string;
  onAction: () => void;
};

type FieldEditModalHeaderActionContextValue = {
  isInFieldEditModal: boolean;
  headerAction?: FieldEditModalHeaderAction;
  registerHeaderAction: (action?: FieldEditModalHeaderAction) => void;
};

const FieldEditModalHeaderActionContext = createContext<FieldEditModalHeaderActionContextValue>({
  isInFieldEditModal: false,
  registerHeaderAction: () => {},
});

export const FieldEditModalHeaderActionProvider = ({ children }: { children: ReactNode }) => {
  const [headerAction, setHeaderAction] = useState<FieldEditModalHeaderAction | undefined>();

  const registerHeaderAction = useCallback((action?: FieldEditModalHeaderAction) => setHeaderAction(action), []);

  const contextValue = useMemo(
    () => ({ isInFieldEditModal: true, headerAction, registerHeaderAction }),
    [headerAction, registerHeaderAction],
  );

  return (
    <FieldEditModalHeaderActionContext.Provider value={contextValue}>
      {children}
    </FieldEditModalHeaderActionContext.Provider>
  );
};

export const useFieldEditModalHeaderAction = () => useContext(FieldEditModalHeaderActionContext);

export const useRegisterFieldEditModalHeaderAction = (action?: FieldEditModalHeaderAction) => {
  const { isInFieldEditModal, registerHeaderAction } = useFieldEditModalHeaderAction();

  // NOTE: Keep `action` out of the effect deps. It is a new object each render, so depending on it loops.
  const actionRef = useRef(action);
  actionRef.current = action;

  const ariaLabel = action?.ariaLabel;

  useEffect(() => {
    if (!isInFieldEditModal || !ariaLabel) {
      return;
    }

    registerHeaderAction({ ariaLabel, onAction: () => actionRef.current?.onAction() });

    return () => registerHeaderAction(undefined);
  }, [isInFieldEditModal, registerHeaderAction, ariaLabel]);

  return isInFieldEditModal;
};
